// src/components/admin/DataIsolationMonitor.jsx
import React, { useState, useEffect } from 'react';
import { FiShield, FiAlertTriangle, FiCheck, FiX, FiRefreshCw, FiDatabase, FiFolder, FiLock } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import toast from 'react-hot-toast';

const DataIsolationMonitor = () => {
  const [isolationStatus, setIsolationStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(null);

  useEffect(() => {
    runIsolationCheck();
  }, []);

  const runIsolationCheck = async () => {
    try {
      setLoading(true);
      
      // Get all tenants
      const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
      const tenants = tenantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const isolationResults = [];
      
      for (const tenant of tenants) {
        const tenantId = tenant.id;
        const checks = {
          tenantId,
          tenantName: tenant.name,
          status: 'healthy',
          issues: [],
          checks: {
            firestoreIsolation: false,
            storageIsolation: false,
            userAccess: false,
            dataLeakage: false,
            securityRules: false
          }
        };
        
        try {
          // 1. Check Firestore data isolation
          const collections = ['clients', 'appointments', 'staff', 'invoices', 'soapNotes'];
          let firestoreIsolated = true;
          
          for (const collectionName of collections) {
            try {
              const tenantCollection = await getDocs(collection(db, 'tenants', tenantId, collectionName));
              const globalCollection = await getDocs(collection(db, collectionName));
              
              // Check if any documents in global collection belong to this tenant
              const globalTenantDocs = globalCollection.docs.filter(doc => 
                doc.data().tenantId === tenantId
              );
              
              if (globalTenantDocs.length > 0) {
                firestoreIsolated = false;
                checks.issues.push(`Found ${globalTenantDocs.length} documents in global ${collectionName} collection`);
              }
            } catch (error) {
              // Collection might not exist, which is fine
            }
          }
          
          checks.checks.firestoreIsolation = firestoreIsolated;
          
          // 2. Check user access isolation
          const tenantUsers = await getDocs(
            query(collection(db, 'users'), where('tenantId', '==', tenantId))
          );
          
          const usersWithoutTenant = await getDocs(
            query(collection(db, 'users'), where('tenantId', '==', null))
          );
          
          let userAccessIsolated = true;
          if (usersWithoutTenant.docs.length > 0) {
            // Check if any users without tenantId have access to this tenant's data
            checks.issues.push(`Found ${usersWithoutTenant.docs.length} users without tenantId`);
            userAccessIsolated = false;
          }
          
          checks.checks.userAccess = userAccessIsolated;
          
          // 3. Check for data leakage between tenants
          let dataLeakageDetected = false;
          
          for (const collectionName of collections) {
            try {
              const tenantDocs = await getDocs(collection(db, 'tenants', tenantId, collectionName));
              
              for (const doc of tenantDocs.docs) {
                const data = doc.data();
                
                // Check if document has correct tenantId
                if (data.tenantId && data.tenantId !== tenantId) {
                  dataLeakageDetected = true;
                  checks.issues.push(`Document ${doc.id} in ${collectionName} has incorrect tenantId: ${data.tenantId}`);
                }
                
                // Check for references to other tenants
                const docString = JSON.stringify(data).toLowerCase();
                for (const otherTenant of tenants) {
                  if (otherTenant.id !== tenantId && docString.includes(otherTenant.id)) {
                    dataLeakageDetected = true;
                    checks.issues.push(`Document ${doc.id} in ${collectionName} contains reference to tenant ${otherTenant.id}`);
                  }
                }
              }
            } catch (error) {
              // Collection might not exist
            }
          }
          
          checks.checks.dataLeakage = !dataLeakageDetected;
          
          // 4. Storage isolation check (simulated)
          checks.checks.storageIsolation = true; // TODO: Implement actual storage check
          
          // 5. Security rules check (simulated)
          checks.checks.securityRules = true; // TODO: Implement actual rules validation
          
          // Determine overall status
          const allChecks = Object.values(checks.checks);
          if (allChecks.every(check => check)) {
            checks.status = 'healthy';
          } else if (allChecks.some(check => check)) {
            checks.status = 'warning';
          } else {
            checks.status = 'critical';
          }
          
        } catch (error) {
          console.error(`Error checking isolation for tenant ${tenantId}:`, error);
          checks.status = 'error';
          checks.issues.push(`Failed to run isolation check: ${error.message}`);
        }
        
        isolationResults.push(checks);
      }
      
      setIsolationStatus(isolationResults);
      setLastCheck(new Date().toISOString());
      
      // Show summary toast
      const healthyCount = isolationResults.filter(r => r.status === 'healthy').length;
      const totalCount = isolationResults.length;
      
      if (healthyCount === totalCount) {
        toast.success(`All ${totalCount} tenants have secure data isolation`);
      } else {
        toast.error(`${totalCount - healthyCount} tenants have isolation issues`);
      }
      
    } catch (error) {
      console.error('Error running isolation check:', error);
      toast.error('Failed to run isolation check');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <FiCheck className="h-5 w-5 text-green-600" />;
      case 'warning':
        return <FiAlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'critical':
        return <FiX className="h-5 w-5 text-red-600" />;
      default:
        return <FiAlertTriangle className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Data Isolation Monitor</h2>
          <p className="text-gray-600">Ensure complete data separation between clinic tenants</p>
        </div>
        <button
          onClick={runIsolationCheck}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Run Check</span>
        </button>
      </div>

      {/* Last Check Info */}
      {lastCheck && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <FiShield className="h-5 w-5 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Last security check: {new Date(lastCheck).toLocaleString()}
              </p>
              <p className="text-sm text-blue-700">
                Monitoring {isolationStatus.length} tenant{isolationStatus.length !== 1 ? 's' : ''} for data isolation
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Running isolation security check...</span>
        </div>
      )}

      {/* Isolation Status Cards */}
      {!loading && isolationStatus.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {isolationStatus.map((tenant) => (
            <div key={tenant.tenantId} className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Card Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {getStatusIcon(tenant.status)}
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">{tenant.tenantName}</h3>
                      <p className="text-sm text-gray-600">{tenant.tenantId}</p>
                    </div>
                  </div>
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(tenant.status)}`}>
                    {tenant.status}
                  </span>
                </div>
              </div>

              {/* Security Checks */}
              <div className="p-6">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Security Checks</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiDatabase className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-700">Firestore Isolation</span>
                    </div>
                    {tenant.checks.firestoreIsolation ? (
                      <FiCheck className="h-4 w-4 text-green-600" />
                    ) : (
                      <FiX className="h-4 w-4 text-red-600" />
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiFolder className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-700">Storage Isolation</span>
                    </div>
                    {tenant.checks.storageIsolation ? (
                      <FiCheck className="h-4 w-4 text-green-600" />
                    ) : (
                      <FiX className="h-4 w-4 text-red-600" />
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiLock className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-700">User Access Control</span>
                    </div>
                    {tenant.checks.userAccess ? (
                      <FiCheck className="h-4 w-4 text-green-600" />
                    ) : (
                      <FiX className="h-4 w-4 text-red-600" />
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiShield className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-700">Data Leakage Prevention</span>
                    </div>
                    {tenant.checks.dataLeakage ? (
                      <FiCheck className="h-4 w-4 text-green-600" />
                    ) : (
                      <FiX className="h-4 w-4 text-red-600" />
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiLock className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-700">Security Rules</span>
                    </div>
                    {tenant.checks.securityRules ? (
                      <FiCheck className="h-4 w-4 text-green-600" />
                    ) : (
                      <FiX className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                </div>

                {/* Issues */}
                {tenant.issues.length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <h5 className="text-sm font-medium text-red-800 mb-2">Security Issues Found:</h5>
                    <ul className="text-sm text-red-700 space-y-1">
                      {tenant.issues.map((issue, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-red-500 mr-2">•</span>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Security Guidelines */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Isolation Guidelines</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Firestore Structure</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div>✓ All tenant data in <code className="bg-gray-100 px-1 rounded">tenants/{'{tenantId}'}/</code></div>
              <div>✓ No cross-tenant document references</div>
              <div>✓ TenantId field in all documents</div>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Storage Structure</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div>✓ Files in <code className="bg-gray-100 px-1 rounded">tenants/{'{tenantId}'}/</code></div>
              <div>✓ No shared file access</div>
              <div>✓ Tenant-specific upload paths</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataIsolationMonitor;
